import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Play, Pause, Square, Clock, Briefcase, ChevronDown, ChevronUp,
  X, Save, Loader2, AlertCircle, StickyNote
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function HeaderBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isStaff, isAdmin, isAttorney } = useAuth();

  // Timer state
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCaseSelector, setShowCaseSelector] = useState(false);

  // Case selection
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loadingCases, setLoadingCases] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');

  // Entry creation
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Notepad state
  const [notes, setNotes] = useState('');
  const [notesExpanded, setNotesExpanded] = useState(false);

  // Load cases on mount
  useEffect(() => {
    if (isStaff || isAdmin || isAttorney) {
      fetchCases();
    }
  }, [isStaff, isAdmin, isAttorney]);

  // Timer logic
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  // Persist timer state to localStorage
  useEffect(() => {
    if (isRunning && startTime) {
      localStorage.setItem('billable_timer', JSON.stringify({
        startTime: startTime.toISOString(),
        caseId: selectedCase?.id,
        caseName: selectedCase?.case_name,
        description,
        notes
      }));
    }
  }, [isRunning, startTime, selectedCase, description, notes]);

  // Restore timer state on mount
  useEffect(() => {
    const saved = localStorage.getItem('billable_timer');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const savedStart = new Date(data.startTime);
        const elapsed = Math.floor((Date.now() - savedStart.getTime()) / 1000);

        if (elapsed > 0 && elapsed < 86400) {
          setStartTime(savedStart);
          setElapsedSeconds(elapsed);
          setIsRunning(true);
          setDescription(data.description || '');
          setNotes(data.notes || '');
          if (data.caseId) {
            setSelectedCase({ id: data.caseId, case_name: data.caseName });
          }
        } else {
          localStorage.removeItem('billable_timer');
        }
      } catch (e) {
        localStorage.removeItem('billable_timer');
      }
    }
  }, []);

  const fetchCases = async () => {
    try {
      setLoadingCases(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cases/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100, status: 'active' }
      });
      const caseList = Array.isArray(response.data) ? response.data : (response.data.cases || []);
      setCases(caseList);
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setLoadingCases(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatHours = (seconds) => {
    return (seconds / 3600).toFixed(2);
  };

  const handleStart = () => {
    // Allow starting without a case - can select later
    setStartTime(new Date());
    setIsRunning(true);
    setError(null);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleResume = () => {
    setIsRunning(true);
  };

  const handleStop = async () => {
    setIsRunning(false);

    if (!selectedCase) {
      setError('Please select a case');
      return;
    }

    if (elapsedSeconds < 60) {
      setError('Minimum time is 1 minute');
      return;
    }

    setIsExpanded(true);
  };

  const handleSaveEntry = async () => {
    if (!selectedCase) {
      setError('Please select a case');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      const hours = parseFloat(formatHours(elapsedSeconds));

      await axios.post(
        `${API_URL}/cases/${selectedCase.id}/time-entries`,
        {
          description: description || `Time tracked on ${new Date().toLocaleDateString()}`,
          hours: hours,
          billable: true,
          date: startTime?.toISOString() || new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Reset timer fully on save — stop the running interval, not just the counter
      setIsRunning(false);
      setElapsedSeconds(0);
      setStartTime(null);
      setDescription('');
      setNotes('');
      setNotesExpanded(false);
      setShowCaseSelector(false);
      setIsExpanded(false);
      localStorage.removeItem('billable_timer');

      setError(null);
    } catch (err) {
      console.error('Error saving time entry:', err);
      setError(err.response?.data?.detail || 'Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setElapsedSeconds(0);
    setStartTime(null);
    setIsRunning(false);
    setDescription('');
    setNotes('');
    setNotesExpanded(false);
    setError(null);
    setIsExpanded(false);
    localStorage.removeItem('billable_timer');
  };

  const handleSelectCase = (caseItem) => {
    setSelectedCase(caseItem);
    setShowCaseSelector(false);
    setCaseSearch('');
  };

  const filteredCases = (cases || []).filter(c => {
    const search = (caseSearch || '').toLowerCase();
    const caseName = (c.case_name || '').toLowerCase();
    const caseNum = String(c.case_number || c.display_number || '').toLowerCase();
    return caseName.includes(search) || caseNum.includes(search);
  });

  // Don't render for non-staff/non-attorney or on login/portal pages
  if (!isStaff && !isAdmin && !isAttorney) return null;
  if (location.pathname === '/login' || location.pathname.startsWith('/portal')) {
    return null;
  }

  const hasTime = elapsedSeconds > 0;

  return (
    <div className="bg-slate-800/80 border-b border-slate-700 px-4 py-2 flex items-center justify-between relative z-[60]">
      {/* Left: spacer */}
      <div className="flex-1" />

      {/* Center/Right: Timer */}
      <div className="flex items-center gap-3">
        {/* Timer Display */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-all ${
            isRunning
              ? 'bg-green-600/20 border border-green-500/50 text-green-400'
              : hasTime
              ? 'bg-amber-600/20 border border-amber-500/50 text-amber-400'
              : 'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500'
          }`}
        >
          <Clock size={16} />
          <span className="font-mono font-medium text-sm">
            {formatTime(elapsedSeconds)}
          </span>
          {selectedCase && (
            <span className="text-xs text-slate-400 max-w-[120px] truncate hidden sm:inline">
              {selectedCase.case_name}
            </span>
          )}
          {isRunning && (
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          )}
          <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>

        {/* Quick Controls */}
        {!isExpanded && (
          <div className="flex items-center gap-1">
            {!isRunning && !hasTime && (
              <button
                onClick={handleStart}
                className="p-1.5 bg-green-600 hover:bg-green-500 rounded text-white transition-colors"
                title="Start Timer"
              >
                <Play size={14} />
              </button>
            )}

            {isRunning && (
              <>
                <button
                  onClick={handlePause}
                  className="p-1.5 bg-amber-600 hover:bg-amber-500 rounded text-white transition-colors"
                  title="Pause"
                >
                  <Pause size={14} />
                </button>
                <button
                  onClick={handleStop}
                  className="p-1.5 bg-red-600 hover:bg-red-500 rounded text-white transition-colors"
                  title="Stop"
                >
                  <Square size={14} />
                </button>
              </>
            )}

            {!isRunning && hasTime && (
              <>
                <button
                  onClick={handleResume}
                  className="p-1.5 bg-green-600 hover:bg-green-500 rounded text-white transition-colors"
                  title="Resume"
                >
                  <Play size={14} />
                </button>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white transition-colors"
                  title="Save Entry"
                >
                  <Save size={14} />
                </button>
                <button
                  onClick={handleDiscard}
                  className="p-1.5 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
                  title="Discard"
                >
                  <X size={14} />
                </button>
              </>
            )}
          </div>
        )}

        {/* Billable Hours Link */}
        <button
          onClick={() => navigate('/billable-hours')}
          className="text-xs text-slate-400 hover:text-white transition-colors hidden sm:block"
        >
          View Stats
        </button>
      </div>

      {/* Expanded Panel Dropdown */}
      {isExpanded && (
        <div className="absolute top-full right-4 mt-1 w-80 bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden z-50">
          {/* Timer Display */}
          <div className="p-4 text-center border-b border-slate-700">
            <div className={`text-3xl font-mono font-bold ${isRunning ? 'text-green-400' : hasTime ? 'text-amber-400' : 'text-slate-400'}`}>
              {formatTime(elapsedSeconds)}
            </div>
            {hasTime && (
              <div className="text-sm text-slate-400 mt-1">
                {formatHours(elapsedSeconds)} hours
              </div>
            )}
          </div>

          {/* Case Selector */}
          <div className="p-3 border-b border-slate-700">
            <label className="block text-xs font-medium text-slate-400 mb-1">Case</label>
            {showCaseSelector ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={caseSearch}
                  onChange={(e) => setCaseSearch(e.target.value)}
                  placeholder="Search cases..."
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {loadingCases ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="animate-spin text-slate-400" size={20} />
                    </div>
                  ) : filteredCases.length === 0 ? (
                    <div className="text-center py-4 text-slate-500 text-sm">No cases found</div>
                  ) : (
                    filteredCases.slice(0, 10).map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCase(c)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-600 transition-colors"
                      >
                        <div className="text-sm text-white truncate">{c.case_name}</div>
                        {c.case_number && (
                          <div className="text-xs text-slate-400">{c.case_number}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
                <button
                  onClick={() => setShowCaseSelector(false)}
                  className="w-full text-center text-xs text-slate-400 hover:text-white py-1"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCaseSelector(true)}
                className="w-full flex items-center justify-between px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-left hover:bg-slate-600 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Briefcase size={14} className="text-slate-400 flex-shrink-0" />
                  <span className={`text-sm truncate ${selectedCase ? 'text-white' : 'text-slate-400'}`}>
                    {selectedCase?.case_name || 'Select a case...'}
                  </span>
                </div>
                <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
              </button>
            )}
          </div>

          {/* Notepad - collapsible */}
          <div className="border-b border-slate-700">
            <button
              onClick={() => setNotesExpanded(!notesExpanded)}
              className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-slate-400">
                <StickyNote size={14} />
                <span className="text-xs font-medium">Notes</span>
                {notes && !notesExpanded && (
                  <span className="text-xs text-slate-500 truncate max-w-[150px]">
                    ({notes.split('\n').length} line{notes.split('\n').length > 1 ? 's' : ''})
                  </span>
                )}
              </div>
              <ChevronUp size={14} className={`text-slate-400 transition-transform ${notesExpanded ? '' : 'rotate-180'}`} />
            </button>
            {notesExpanded && (
              <div className="px-3 pb-3">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Quick notes while on the call..."
                  rows={4}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            )}
          </div>

          {/* Description */}
          {hasTime && !isRunning && (
            <div className="p-3 border-b border-slate-700">
              <label className="block text-xs font-medium text-slate-400 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you work on?"
                rows={2}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-red-500/20 border-b border-red-500/30">
              <div className="flex items-center gap-2 text-red-400 text-xs">
                <AlertCircle size={14} />
                {error}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="p-3 flex gap-2">
            {!isRunning && !hasTime && (
              <button
                onClick={handleStart}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-medium transition-colors"
              >
                <Play size={16} />
                Start
              </button>
            )}

            {isRunning && (
              <>
                <button
                  onClick={handlePause}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium transition-colors"
                >
                  <Pause size={16} />
                  Pause
                </button>
                <button
                  onClick={handleStop}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white font-medium transition-colors"
                >
                  <Square size={16} />
                </button>
              </>
            )}

            {!isRunning && hasTime && (
              <>
                <button
                  onClick={handleResume}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white transition-colors"
                >
                  <Play size={16} />
                </button>
                <button
                  onClick={handleSaveEntry}
                  disabled={saving || !selectedCase}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Entry
                </button>
                <button
                  onClick={handleDiscard}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white transition-colors"
                  title="Discard"
                >
                  <X size={16} />
                </button>
              </>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default HeaderBar;
